import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';

const PasswordChangeUtility = () => {
  const [email, setEmail] = useState('mike@poetsroad.com');
  const [newPassword, setNewPassword] = useState('MonkeyRole21!');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handlePasswordChange = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const { data: userProfile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('id, email, full_name')
        ?.eq('email', email?.toLowerCase()?.trim())
        ?.single();

      if (profileError) {
        if (profileError?.code === 'PGRST116') {
          setError(`No user found with email: ${email}`);
        } else {
          setError(`Error finding user: ${profileError?.message}`);
        }
        return;
      }

      const { data, error: updateError } = await supabase?.auth?.admin?.updateUserById(
        userProfile?.id,
        { password: newPassword }
      );

      if (updateError) {
        if (updateError?.message?.includes('admin')) {
          setError('Admin privileges required. Please use Supabase Dashboard Auth section to update user passwords.');
        } else {
          setError(`Error updating password: ${updateError?.message}`);
        }
        return;
      }

      setResult({
        success: true,
        message: `Password successfully updated for ${userProfile?.full_name} (${email})`,
        user: userProfile
      });

    } catch (error) {
      console.error('Password change error:', error);
      setError(`Unexpected error: ${error?.message || 'Please try again'}`);
    } finally {
      setLoading(false);
    }
  };

  const createUserIfNotExists = async () => {
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const { data: authData, error: authError } = await supabase?.auth?.admin?.createUser({
        email: email?.toLowerCase()?.trim(),
        password: newPassword,
        email_confirm: true
      });

      if (authError) {
        setError(`Error creating user: ${authError?.message}`);
        return;
      }

      const { data: profileData, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.insert({
          id: authData?.user?.id,
          email: email?.toLowerCase()?.trim(),
          full_name: email?.split('@')?.[0] || 'Mike',
          role: 'member'
        })
        ?.select()
        ?.single();

      if (profileError) {
        setError(`User created but profile creation failed: ${profileError?.message}`);
        return;
      }

      setResult({
        success: true,
        message: `User ${email} created successfully with password: ${newPassword}`,
        user: profileData,
        authUser: authData?.user
      });

    } catch (error) {
      console.error('User creation error:', error);
      setError(`Unexpected error: ${error?.message || 'Please try again'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 mt-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">
        Password Change Utility
      </h3>
      <form onSubmit={handlePasswordChange} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            User Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e?.target?.value)}
            className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            New Password
          </label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e?.target?.value)}
            className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
            minLength="6"
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Minimum 6 characters required
          </p>
        </div>

        <div className="flex space-x-3">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
          
          <button
            type="button"
            onClick={createUserIfNotExists}
            disabled={loading}
            className="px-4 py-2 bg-success text-success-foreground rounded-md hover:bg-success/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Creating...' : 'Create User'}
          </button>
        </div>
      </form>

      {result && (
        <div className="mt-4 p-4 bg-success/10 border border-success/20 rounded-md">
          <h4 className="text-success font-medium">Success!</h4>
          <p className="text-success/80 text-sm mt-1">{result?.message}</p>
          {result?.user && (
            <div className="mt-2 text-xs text-success/70">
              <p>User ID: {result?.user?.id}</p>
              <p>Email: {result?.user?.email}</p>
              <p>Role: {result?.user?.role}</p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <h4 className="text-destructive font-medium">Error</h4>
          <p className="text-destructive/80 text-sm mt-1">{error}</p>
          
          {error?.includes('admin') && (
            <div className="mt-3 p-3 bg-warning/10 border border-warning/20 rounded-md">
              <h5 className="text-warning font-medium text-sm">Alternative Solution:</h5>
              <p className="text-warning/80 text-xs mt-1">
                1. Go to your Supabase Dashboard<br/>
                2. Navigate to Authentication → Users<br/>
                3. Find user: {email}<br/>
                4. Click on user and update password to: {newPassword}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="mt-6 p-4 bg-muted/30 rounded-md">
        <h4 className="text-foreground font-medium text-sm">Instructions:</h4>
        <ul className="text-muted-foreground text-xs mt-2 space-y-1">
          <li>• <strong>Update Password:</strong> Change password for existing user</li>
          <li>• <strong>Create User:</strong> Create new user if they don't exist</li>
          <li>• Admin privileges may be required for password updates</li>
          <li>• Check Supabase Dashboard if admin API access is restricted</li>
        </ul>
      </div>
    </div>
  );
};

export default PasswordChangeUtility;